<template>
  <v-container fluid>
    <v-row v-if="!schemaConfig[currentDivision]">
      <v-alert border="top" colored-border type="warning" elevation="2">
        Schema management does not currently support the selected division.
      </v-alert>
    </v-row>
    <v-row v-if="schemaConfig[currentDivision]">
      <v-col cols="3">
        <v-card height="600" width="256" class="mx-auto">
          <v-navigation-drawer permanent>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title class="title">
                  Schema Registry
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ divisionNames[currentDivision] }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <v-divider></v-divider>

            <v-list dense nav>
              <v-list-item
                v-for="entity in schemaEntities"
                :key="entity"
                :class="entity == selectedEntity ? 'highlighted' : ''"
                v-on:click="
                  editSchema(
                    entity,
                    selectedEntity == '' || entity == selectedEntity
                      ? selectedVersion
                      : 'LATEST'
                  )
                "
                link
              >
                <v-list-item-content>
                  <v-list-item-title
                    >{{ entity.toUpperCase() }}
                  </v-list-item-title>
                </v-list-item-content>

                <div class="text-center d-inline version-btn ml-5">
                  <v-menu v-if="entity == selectedEntity" offset-y>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        color="blue-grey"
                        dark
                        small
                        icon
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ selectedVersion }}
                      </v-btn>
                    </template>

                    <v-list v-if="schemaVersions.length > 0">
                      <v-list-item
                        v-for="version in schemaVersions"
                        :key="version.version"
                        v-on:click="editSchema(entity, version.version)"
                        link
                      >
                        <v-list-item-title>
                          <div
                            v-if="version.active"
                            class="text-sm-caption green--text"
                            v-text="version.version"
                          ></div>
                          <div
                            v-else
                            class="text-sm-caption red--text"
                            v-text="version.version"
                          ></div>
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </v-list-item>
            </v-list>
          </v-navigation-drawer>
        </v-card>
      </v-col>
      <v-col cols="9">
        <ace-editor
          height="600"
          :readOnly="readOnly"
          :on-change="onSchemaChange"
          :content="schemaDefinition"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import AceEditor from '@/components/AceEditor';
import Ajv from 'ajv';
import { workersConfig } from '@/store/modules/workers.js';

const { mapState, mapActions } = createNamespacedHelpers('schemareg');
const { mapState: mapStateConfig } = createNamespacedHelpers('config');

export default {
  components: { AceEditor },
  name: 'SchemaRegistry',
  async created() {
    await this.getLatestSchemas(this.currentDivision);
    this.editSchema(this.schemaEntities[0], 'LATEST');
  },
  methods: {
    ...mapActions(['getLatestSchemas']),
    editSchema: function(entity, version) {
      this.selectedEntity = entity;
      this.selectedVersion =
        version == 'LATEST'
          ? this.schemas[this.selectedEntity]
            ? this.schemas[this.selectedEntity][0].version
            : ''
          : version;
      this.schemaDefinitionToSave = this.schemaDefinition;
    },
    resetSchema: function() {
      this.selectedEntity = '';
    },
    onSchemaChange: function(val) {
      this.schemaDefinitionToSave = val;
      try {
        this.schemaDefinitionToSave = JSON.stringify(JSON.parse(val), null, 4);
        this.isValidSchema = true;
      } catch (error) {
        this.isValidSchema = false;
      }
    }
  },
  watch: {
    currentDivision() {
      this.selectedEntity = '';
      let serv = this;
      this.getLatestSchemas(this.currentDivision).then(function() {
        serv.editSchema(serv.schemaEntities[0], 'LATEST');
      });
    }
  },
  data: function() {
    return {
      schemaConfig: workersConfig.schemas,
      divisionNames: { FBP: 'Fragrance & Beauty', FSH: 'Fashion', WFJ: 'Watches & Fine Jewelry' },
      selectedEntity: '',
      selectedVersion: '',
      readOnly: true,
      schemaDefinitionToSave: '',
      isValidSchema: true,
      testDocument: null,
      searchDocument: null,
      showTestDocumentDialog: false
    };
  },
  computed: {
    ...mapState(['schemas', 'loading']),
    ...mapStateConfig(['entities', 'currentDivision']),
    currentDivisionEntities() {
      return this.entities[this.currentDivision];
    },
    schemaEntities() {
      if (this.schemaConfig[this.currentDivision]) {
        return Object.keys(this.schemaConfig[this.currentDivision]);
      } else {
        return [];
      }
    },
    schemaDefinition() {
      if (this.selectedEntity in this.schemas) {
        for (let schema of this.schemas[this.selectedEntity]) {
          if (schema.version && schema.version == this.selectedVersion) {
            return JSON.stringify(schema.schema, null, 4);
          } else if (schema.message) {
            return schema.message;
          }
        }
      }
      return '';
    },
    testButton() {
      return this.testDocument
        ? 'Document is ' + (this.schemaValidation.isValid ? 'valid' : 'invalid')
        : '+ ADD TEST DOCUMENT';
    },
    schemaValidation() {
      try {
        if (this.testDocument == null) return { isValid: true, message: '' };
        const ajv = new Ajv();
        const jsonSchema = ajv.compile(JSON.parse(this.schemaDefinitionToSave));
        const validation = jsonSchema(this.testDocument);
        return {
          isValid: validation,
          message: validation ? [] : jsonSchema.errors
        };
      } catch (error) {
        return {
          isValid: false,
          message: [{ message: error.message }]
        };
      }
    },
    schemaVersions() {
      const versions = [];
      if (this.selectedEntity in this.schemas) {
        const schema = this.schemas[this.selectedEntity];
        for (let index = 0; index < schema.length; index++) {
          const schemaVersion = schema[index].version;
          if (this.selectedVersion != schemaVersion)
            versions.push({
              version: schemaVersion,
              active: schema[index].active
            });
        }
      }
      return versions;
    }
  }
};
</script>

<style>
.highlighted {
  background-color: #ededf3;
}
</style>
